import { useTranslation } from "react-i18next"

import type { PresentationBrandResponse } from "@/api/admin/skills"

import { Check } from "@/components/icons"
import { Button } from "@/components/ui/button"

import {
  presentationBrandChecklist,
  presentationBrandStatus,
} from "./presentation-brand-spec-utils"

type PresentationBrandStatusCardProps = {
  data: PresentationBrandResponse
  pending: boolean
  onGoLive: () => void
  onUseTemplate: () => void
}

export function PresentationBrandStatusCard({
  data,
  pending,
  onGoLive,
  onUseTemplate,
}: PresentationBrandStatusCardProps) {
  const { t } = useTranslation()
  const status = presentationBrandStatus(data)
  const checklist = presentationBrandChecklist(data)
  const canGoLive = checklist.specSaved && checklist.frameLogo && status !== "live"

  const statusCopy = {
    live: {
      label: t("adminDashboard.systemConfig.presentationBrand.statusLive", {
        defaultValue: "Live — markdown spec",
      }),
      detail: t("adminDashboard.systemConfig.presentationBrand.statusLiveDetail", {
        defaultValue: "New decks follow this spec. Users are not asked.",
      }),
      className: "border-success/30 bg-success/10 text-success",
    },
    incomplete: {
      label: t("adminDashboard.systemConfig.presentationBrand.statusIncomplete", {
        defaultValue: "Incomplete — spec not live",
      }),
      detail: t("adminDashboard.systemConfig.presentationBrand.statusIncompleteDetail", {
        defaultValue: "Save the spec file, upload the frame logo, then go live.",
      }),
      className: "border-warning/30 bg-warning/10 text-warning-foreground",
    },
    template: {
      label: t("adminDashboard.systemConfig.presentationBrand.statusTemplate", {
        defaultValue: "Company PowerPoint template",
      }),
      detail: t("adminDashboard.systemConfig.presentationBrand.statusTemplateDetail", {
        defaultValue: "New decks always fill the company .pptx. Users are not asked.",
      }),
      className: "border-border bg-muted/40 text-muted-foreground",
    },
    ask: {
      label: t("adminDashboard.systemConfig.presentationBrand.statusAsk", {
        defaultValue: "Ask the user",
      }),
      detail: t("adminDashboard.systemConfig.presentationBrand.statusAskDetail", {
        defaultValue: "Explorer asks whether to use the company template before building a deck.",
      }),
      className: "border-border bg-muted/40 text-muted-foreground",
    },
  }[status]

  const checks = [
    {
      done: checklist.specSaved,
      label: t("adminDashboard.systemConfig.presentationBrand.checkSpec", {
        defaultValue: "Spec file saved",
      }),
    },
    {
      done: checklist.frameLogo,
      label: t("adminDashboard.systemConfig.presentationBrand.checkLogo", {
        defaultValue: "Frame logo uploaded",
      }),
    },
    {
      done: checklist.enabled,
      label: t("adminDashboard.systemConfig.presentationBrand.checkEnabled", {
        defaultValue: "Spec enabled",
      }),
    },
    {
      done: checklist.extraLogos,
      optional: true,
      label: t("adminDashboard.systemConfig.presentationBrand.checkExtras", {
        defaultValue: "Extra logos (optional)",
      }),
    },
  ]

  return (
    <div className={`space-y-3 rounded-lg border p-4 ${statusCopy.className}`}>
      <div className="space-y-1">
        <p className="font-KHTeka-Regular text-base">{statusCopy.label}</p>
        <p className="text-xs opacity-90">{statusCopy.detail}</p>
      </div>
      <ul className="space-y-1.5">
        {checks.map((item) => (
          <li key={item.label} className="flex items-center gap-2 text-xs">
            <span
              className={`flex size-4 items-center justify-center rounded-sm ${
                item.done
                  ? "bg-success text-success-foreground"
                  : "border-border bg-background border"
              }`}
            >
              {item.done ? <Check className="size-3" /> : null}
            </span>
            <span>
              {item.label}
              {item.optional && !item.done
                ? ` — ${t("adminDashboard.systemConfig.presentationBrand.optional", { defaultValue: "optional" })}`
                : ""}
            </span>
          </li>
        ))}
      </ul>
      <div className="flex flex-wrap gap-2">
        {canGoLive ? (
          <Button disabled={pending} size="sm" type="button" onClick={onGoLive}>
            {t("adminDashboard.systemConfig.presentationBrand.goLive", {
              defaultValue: "Go live",
            })}
          </Button>
        ) : null}
        {status === "live" ? (
          <Button
            disabled={pending}
            size="sm"
            type="button"
            variant="outline"
            onClick={onUseTemplate}
          >
            {t("adminDashboard.systemConfig.presentationBrand.useTemplate", {
              defaultValue: "Use company template instead",
            })}
          </Button>
        ) : null}
      </div>
    </div>
  )
}
